import graphql__458ab3bba81e16edb8af from "./__generated__/EmailTemplateList_query.graphql.ts";import * as React from "react"
import {graphql} from "react-relay";
import {useFragment} from "react-relay/hooks";
import Table from "./presentational/table/Table";
import TableHeader from "./presentational/table/TableHeader";
import TableData, {TextAlignment} from "./presentational/table/TableData";
import {useNavigate} from "react-router";
import {useIntl} from "react-intl";

type Props = {
    queryRef: any
    searchTerm?: string
    onLoadingChange?: (isLoading: boolean) => void
    showUsedOneTime?: boolean
}

const EmailTemplateList: React.FC<Props> = (props) => {
    const intl = useIntl();
    const {showUsedOneTime = true} = props;
    const data = useFragment(
        graphql__458ab3bba81e16edb8af,
        props.queryRef
    );
    const navigate = useNavigate();

    // Filter templates by search term and showUsedOneTime on client side
    const filteredTemplates = data.emailTemplates?.filter(template => {
        // Filter by search term
        if (props.searchTerm) {
            const searchLower = props.searchTerm.toLowerCase();
            if (!template.name.toLowerCase().includes(searchLower)) {
                return false;
            }
        }

        // Filter by showUsedOneTime: hide used one-time templates unless checkbox is checked
        if (!showUsedOneTime) {
            // Template is hidden if: not reusable AND has been sent
            if (!template.reusable && template.sentWithMarketingEmailAt !== null) {
                return false;
            }
        }

        return true;
    }) || [];

    if (filteredTemplates.length === 0) {
        return (
            <div className="px-6 pb-6 text-gray-500 text-center">
                {intl.formatMessage({id: 'view.emailTemplates.noEmailTemplates'})}
            </div>
        );
    }

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    const getTypeBadge = (template: {reusable: boolean, sentWithMarketingEmailAt: string | null}) => {
        if (template.reusable) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {intl.formatMessage({id: 'list.emailTemplate.type.reusable'})}
                </span>
            );
        }
        if (template.sentWithMarketingEmailAt) {
            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    {intl.formatMessage({id: 'list.emailTemplate.type.used'})}
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                {intl.formatMessage({id: 'list.emailTemplate.type.oneTime'})}
            </span>
        );
    };

    return (
        <div className="flex-1 min-h-0 relative overflow-y-auto" aria-label={intl.formatMessage({id: 'navigation.emailTemplates'})}>
            <Table>
                <thead>
                    <tr>
                        <TableHeader>{intl.formatMessage({id: 'list.emailTemplate.header.name'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.emailTemplate.header.type'})}</TableHeader>
                        <TableHeader className="text-right">{intl.formatMessage({id: 'list.emailTemplate.header.usageCount'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.emailTemplate.header.createdAt'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.emailTemplate.header.updatedAt'})}</TableHeader>
                    </tr>
                </thead>
                <tbody>
                    {filteredTemplates.map(
                        (template, index) => {
                            return (
                                <tr
                                    key={template.id}
                                    className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 cursor-pointer'}
                                    onClick={() => navigate("/marketing/templates/" + template.id)}
                                >
                                    <TableData>
                                        <span className="font-medium">{template.name}</span>
                                    </TableData>
                                    <TableData>
                                        {getTypeBadge(template)}
                                    </TableData>
                                    <TableData textAlignment={TextAlignment.Right}>
                                        {template.usageCount}
                                    </TableData>
                                    <TableData>
                                        {formatDate(template.createdAt)}
                                    </TableData>
                                    <TableData>
                                        {formatDate(template.updatedAt)}
                                    </TableData>
                                </tr>
                            )
                        }
                    )}
                </tbody>
            </Table>
        </div>
    )
}

export default EmailTemplateList;
